import { computed } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { LoginEvents, Screens, type ScreenChangeData } from '@common/auth-layer/types'
import { MIGRATION_GATE_PATHS_SET } from '@common/auth-layer/utils/migration'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useRoute, useRouter } from 'nuxt/app'
import type { RouteName } from '@common/navigation-layer/config/manual-routes'

/**
 * Screen to route name mapping for auth pages
 */
const SCREEN_ROUTE_NAMES: Record<string, RouteName> = {
  [Screens.LOGIN]: 'auth-login',
  [Screens.LOGIN_WITH_EMAIL]: 'auth-login',
  [Screens.PROCEED_TO_LOGIN]: 'auth-login',
  [Screens.SIGN_UP]: 'auth-signup',
  [Screens.FORGOT_PASSWORD]: 'auth-forgot-password',
  [Screens.LOGIN_WITH_SECURE_CODE]: 'auth-login-with-code',
  [Screens.VERIFY_SECURE_CODE]: 'auth-login-with-code', // Same page handles verification
  [Screens.COMPLETE_SIGNUP]: 'auth-login-with-code', // Stays on login-with-code page (unknown-user handoff)
  [Screens.EXPIRED_PASSWORD]: 'auth-expired-password',
  [Screens.PROFILE_CONFIRMATION]: 'auth-profile-confirmation',
}

/**
 * Get the route name for a given screen
 */
export const getScreenRouteName = (screen: string): RouteName => {
  return SCREEN_ROUTE_NAMES[screen] || 'auth-login'
}

/**
 * Whether a screen change should stay on the current page (no route navigation).
 *
 * These are the screens that render in-place via `AuthHandler` rather than as
 * their own route: signup-internal OTP verify/back, the
 * forgot-password → proceed-to-login intermediate, and any target that maps to
 * the route the user is already on.
 *
 * Single source of truth for both `createChangeScreenHandler` (navigate iff not
 * in-place) and `AuthPageLayoutWeb` (mutate local `currentScreen` iff in-place),
 * so the two never disagree and routed changes are driven purely by the route —
 * avoiding an instant local swap that pre-empts the page transition.
 */
export const screenChangeStaysInPlace = (currentScreen: string, targetScreen: string): boolean => {
  if (currentScreen === Screens.SIGN_UP && (targetScreen === Screens.VERIFY_SECURE_CODE || targetScreen === Screens.SIGN_UP)) {
    return true
  }

  if (targetScreen === Screens.PROCEED_TO_LOGIN && currentScreen === Screens.FORGOT_PASSWORD) return true

  return getScreenRouteName(targetScreen) === getScreenRouteName(currentScreen)
}

/**
 * Composable for auth page shared logic
 * Handles redirects, navigation between auth screens, and login callbacks
 */
export const useAuthPage = () => {
  const route = useRoute()
  const router = useRouter()
  const { CPNavigateTo } = useCPNavigation()
  const auth = useAuth()

  // Get redirect URL from query params
  const redirectUrl = computed(() => {
    const redirect = route.query.redirect as string | undefined
    return redirect || null
  })

  // Get pre-filled email from query params
  const initialEmail = computed(() => {
    return (route.query.email as string) || ''
  })

  // Get referrer user from query params (for affiliate referrals)
  const referrerUser = computed(() => {
    const refCode = route.query.ref as string | undefined
    if (refCode && process.client) {
      window.localStorage.setItem('refCode', refCode)
    }
    return undefined
  })

  /**
   * Build query params preserving redirect and optionally adding email.
   * Password login (`/login`) must not put email in the URL or history.state.
   */
  const buildQueryParams = (email?: string): Record<string, string> | undefined => {
    const query: Record<string, string> = {}
    if (redirectUrl.value) {
      query.redirect = redirectUrl.value
    }
    if (email) {
      query.email = email
    }
    return Object.keys(query).length > 0 ? query : undefined
  }

  /** Screens that share `/login` — never leak email into the query string. */
  const isPasswordLoginScreen = (page: string) =>
    page === Screens.LOGIN || page === Screens.LOGIN_WITH_EMAIL || page === Screens.PROCEED_TO_LOGIN

  const handleLoginCallback = async (event?: { eventType?: string }) => {
    const currentPath = router.currentRoute.value.path
    const pathOnly = currentPath.split('?')[0]
    const isProfileConfirmationPath = pathOnly === '/profile-confirmation' || pathOnly.endsWith('/profile-confirmation')

    const isGateCleared =
      event?.eventType === LoginEvents.MIGRATION_GATE_CLEARED ||
      (event?.eventType === LoginEvents.LOGIN_SUCCESSFUL && isProfileConfirmationPath)

    // saveAuthAfterLogin has already navigated to a migration gate page.
    // Don't clobber that with a dashboard redirect.
    if (!isGateCleared && MIGRATION_GATE_PATHS_SET.has(pathOnly)) {
      return
    }

    if (route.query.redirect) {
      // For a normal login, saveAuthAfterLogin already triggered the
      // redirect. For a cleared migration gate, the gate intercepted the
      // original login's redirect, so we have to resume it here.
      if (isGateCleared) {
        await auth.handleRedirectAfterLogin()
      }
      return
    }

    await CPNavigateTo({ name: 'index' })
  }

  /**
   * Handle screen change - navigate to appropriate auth page
   * @param currentScreen - The current screen (to avoid unnecessary navigation)
   */
  const createChangeScreenHandler = (currentScreen: Screens) => {
    return async (data: ScreenChangeData) => {
      if (!data.page) return

      // In-place screens (signup-internal verify/back,
      // forgot→proceed, same-route) render via AuthHandler without a route
      // change. Only routed screens navigate — see screenChangeStaysInPlace.
      if (screenChangeStaysInPlace(currentScreen, data.page)) return

      const omitEmailFromQuery = isPasswordLoginScreen(data.page)
      await CPNavigateTo({
        name: getScreenRouteName(data.page),
        query: buildQueryParams(omitEmailFromQuery ? undefined : data.email),
        // Clear any prior OTP/magic-link history.state.email so the password
        // form does not prefill (and the address bar stays email-free).
        ...(omitEmailFromQuery ? { state: { email: '' } } : {}),
      })
    }
  }

  return {
    // Reactive state
    redirectUrl,
    initialEmail,
    referrerUser,

    // Handlers
    handleLoginCallback,
    createChangeScreenHandler,
    buildQueryParams,
  }
}
